import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { useOnboarding } from '../../../context/OnboardingContext';
import { PrivateWizardLayout, WizardContainer, WizardCard, WizardActions } from '../WizardLayout';
import Stepper from '../Stepper';
import { generateAIPreview, saveTestCases } from '../../../services/wizardApi';
import { toast } from 'react-toast';
import axiosInstance from '../../../api/axiosInstance';
import {
  LuBot, LuActivity, LuFileText, LuTarget, LuTrendingUp,
  LuLightbulb, LuCheck, LuInfo,
  LuLink, LuPlus, LuRefreshCw, LuTrash, LuList, LuLayoutGrid,
  LuInbox, LuCopy, LuChevronUp, LuChevronDown, LuPencil, LuX, LuVideo, LuRadar,
  LuChevronRight
} from 'react-icons/lu';
import { TEST_CREATION_MODES, UI_MAP_STATUS } from '../../../Config/Onboarding/constant';
import DeleteConfirmationModal from '../../test-plans/DeleteConfirmationModal';
import { useAppDialog } from '../../../context/DialogContext';

const Step6AAIPreview = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { intent, project, uiMap, testCases, setTestCases, nextStep, previousStep, engine, testCreationMode } = useOnboarding();
  const { confirm } = useAppDialog();
  const [cases, setCases] = useState(testCases);
  const [requirements, setRequirements] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [selectedCases, setSelectedCases] = useState([]);
  const [expandedCase, setExpandedCase] = useState(null);
  const [editingCase, setEditingCase] = useState(null);
  const [editingStep, setEditingStep] = useState(null);
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'grid'
  const [filterPriority, setFilterPriority] = useState('all');
  const [showInsights, setShowInsights] = useState(true);
  const [showTraceability, setShowTraceability] = useState(false);
  const [mappingCase, setMappingCase] = useState(null);
  const [showUIMapWarning, setShowUIMapWarning] = useState(false);
  const [wizardTestData, setWizardTestData] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [testCaseToDelete, setTestCaseToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [expandedHierarchy, setExpandedHierarchy] = useState({
    epics: {},
    stories: {},
    testCases: {}
  });

  // Check if this is a UI project (WEB/MOBILE)
  const isUIProject = project.applicationType === 'web' || project.applicationType === 'mobile';
  const hasUIMap = uiMap.status === UI_MAP_STATUS.READY;
  const urlMode = searchParams.get('mode');
  const isUIDiscoveryMode =
    testCreationMode === TEST_CREATION_MODES.UI_DISCOVERY || urlMode === 'ui';
  const step7ModeQuery = isUIDiscoveryMode ? 'ui' : 'ai';

  useEffect(() => {
    loadPreview(filterPriority);
  }, [filterPriority]);

  const extractTestCasesFromAPI = () => {
    if (!wizardTestData?.epics) return [];
    
    const extracted = [];
    wizardTestData.epics.forEach((epic) => {
      if (epic.stories) {
        epic.stories.forEach((story) => {
          if (story.test_cases) {
            story.test_cases.forEach((testCase) => {
              extracted.push({
                id: testCase.test_case_id.toString(),
                test_case_name: testCase.name,
                scenario_name: `Scenario: ${testCase.name}`,
                priority: testCase.priority,
                status: testCase.status,
                tags: [epic.epic_key, story.story_key],
                steps: testCase.steps?.step_description?.map((desc, idx) => ({
                  step: idx + 1,
                  action: desc,
                  expected: testCase.steps?.expected_result?.[idx] || ''
                })) || [],
                epic_key: epic.epic_key,
                epic_name: epic.summary,
                story_key: story.story_key,
                story_name: story.summary,
                mapped_requirements: []
              });
            });
          }
        });
      }
    });
    return extracted;
  };

  const apiTestCases = extractTestCasesFromAPI();

  useEffect(() => {
    if (apiTestCases.length > 0) {
      setCases(apiTestCases);
    }
  }, [wizardTestData]);

  const loadPreview = async (priority = 'all') => {
    try {
      setIsLoading(true);
      let url = `/wizard/get_wizard_test_case_data?project_id=${project.project_id}`;
      if (priority !== 'all') {
        url += `&priority=${priority}`;
      }
      
      const response = await axiosInstance.get(url);
      
      if (response) {
        setWizardTestData(response);
        setExpandedHierarchy({
          epics: {},
          stories: {},
          testCases: {}
        });
      }
    } catch (error) {
      console.error('Error loading preview:', error);
      toast.error('Failed to load preview data');
    } finally {
      setIsLoading(false);
    }
  };

  const handleContinue = async () => {
    // UI Discovery flow: after AI Preview, continue to Discovery Setup
    if (isUIDiscoveryMode) {
      setIsSaving(true);
      try {
        // await saveTestCases(cases);
        setTestCases(cases);
        nextStep();
        navigate(`/app/onboarding?step=7&mode=${step7ModeQuery}`);
        toast.success('Test cases saved successfully');
      } catch (error) {
        toast.error('Failed to save test cases');
      } finally {
        setIsSaving(false);
      }
      return;
    }

    setShowUIMapWarning(true);
    return;
    // For UI projects without UI Map, show warning modal
    // if (isUIProject && !hasUIMap) {
    //   setShowUIMapWarning(true);
    //   return;
    // }

    setIsSaving(true);
    try {
      await saveTestCases(cases);
      setTestCases(cases);
      nextStep();
      if (project.applicationType === 'api') {
        navigate(`/app/onboarding?step=7&mode=${step7ModeQuery}`);
      } else {
        navigate(`/app/onboarding?step=7&mode=${step7ModeQuery}`);
      }
      toast.success('Test cases saved successfully');
    } catch (error) {
      toast.error('Failed to save test cases');
    } finally {
      setIsSaving(false);
    }
  };

  const handleTestCasesOnly = async () => {
    setIsSaving(true);
    try {
      await saveTestCases(cases);
      setTestCases(cases);
      toast.success('Test cases saved successfully');
      toast.info('Scripts will be generated after UI Map is created');
      setShowUIMapWarning(false);
      navigate('/dashboard');
    } catch (error) {
      toast.error('Failed to save test cases');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSelectCase = (caseId) => {
    setSelectedCases(prev => 
      prev.includes(caseId) 
        ? prev.filter(id => id !== caseId)
        : [...prev, caseId]
    );
  };

  const handleSelectAll = () => {
    if (selectedCases.length === filteredCases.length) {
      setSelectedCases([]);
    } else {
      setSelectedCases(filteredCases.map(c => c.id));
    }
  };

  const handleDeleteSelected = async () => {
    if (!selectedCases.length) return;
    const ok = await confirm({
      title: 'Delete test cases?',
      body: `Delete ${selectedCases.length} selected test case(s)? This cannot be undone.`,
      confirmLabel: 'Delete',
      cancelLabel: 'Cancel',
      danger: true,
    });
    if (!ok) return;
    setCases((prev) => prev.filter((c) => !selectedCases.includes(c.id)));
    setSelectedCases([]);
    toast.success('Test cases deleted');
  };

  const handleRegenerateSelected = async () => {
    toast.info('Regenerating selected test cases...');
    // Simulate regeneration
    setTimeout(() => {
      toast.success('Test cases regenerated');
    }, 1500);
  };

  const handleAddTestCase = () => {
    const newCase = {
      id: `tc_${Date.now()}`,
      test_case_name: 'New Test Case',
      scenario_name: 'Scenario: New Test Case',
      steps: [
        { step: 1, action: 'Navigate to page', expected: 'Page loads successfully' }
      ],
      priority: 'Medium',
      tags: ['Custom'],
      editable: true
    };
    setCases(prev => [...prev, newCase]);
    setEditingCase(newCase.id);
    setExpandedCase(newCase.id);
    toast.success('New test case added');
  };

  const handleUpdateCase = (caseId, updates) => {
    setCases(prev => prev.map(c => 
      c.id === caseId ? { ...c, ...updates } : c
    ));
  };

  const handleAddStep = (caseId) => {
    const testCase = cases.find(c => c.id === caseId);
    const newStep = {
      step: testCase.steps.length + 1,
      action: 'New action',
      expected: 'Expected result'
    };
    handleUpdateCase(caseId, {
      steps: [...testCase.steps, newStep]
    });
  };

  const handleUpdateStep = (caseId, stepIndex, updates) => {
    const testCase = cases.find(c => c.id === caseId);
    const updatedSteps = testCase.steps.map((s, i) => 
      i === stepIndex ? { ...s, ...updates } : s
    );
    handleUpdateCase(caseId, { steps: updatedSteps });
  };

  const handleDeleteStep = (caseId, stepIndex) => {
    const testCase = cases.find(c => c.id === caseId);
    const updatedSteps = testCase.steps
      .filter((_, i) => i !== stepIndex)
      .map((s, i) => ({ ...s, step: i + 1 }));
    handleUpdateCase(caseId, { steps: updatedSteps });
  };

  const handleDuplicateCase = (caseId) => {
    const original = cases.find(c => c.id === caseId);
    const duplicate = {
      ...original,
      id: `tc_${Date.now()}`,
      test_case_name: `${original.test_case_name} (Copy)`,
      scenario_name: `${original.scenario_name} (Copy)`
    };
    setCases(prev => [...prev, duplicate]);
    toast.success('Test case duplicated');
  };

  const handleDeleteTestCaseClick = (testCase) => {
    setTestCaseToDelete(testCase);
    setShowDeleteConfirm(true);
  };

  const confirmDeleteTestCase = async () => {
    if (!testCaseToDelete) return;

    setIsDeleting(true);
    try {
      await axiosInstance.delete(`/test_case/${testCaseToDelete.test_case_id}`);

      setWizardTestData(prev => {
        if (!prev?.epics) return prev;
        return {
          ...prev,
          epics: prev.epics.map(epic => ({
            ...epic,
            stories: epic.stories?.map(story => ({
              ...story,
              test_cases: story.test_cases?.filter(
                tc => tc.test_case_id !== testCaseToDelete.test_case_id
              )
            }))
          }))
        };
      });

      toast.success('Test case deleted successfully');
      setShowDeleteConfirm(false);
      setTestCaseToDelete(null);
    } catch (error) {
      console.error('Error deleting test case:', error);
      toast.error('Failed to delete test case. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleToggleRequirement = (caseId, requirementId) => {
    setCases(prev => prev.map(c => {
      if (c.id !== caseId) return c;
      
      const currentMappings = c.mapped_requirements || [];
      const isCurrentlyMapped = currentMappings.some(r => r.id === requirementId);
      
      if (isCurrentlyMapped) {
        return {
          ...c,
          mapped_requirements: currentMappings.filter(r => r.id !== requirementId)
        };
      } else {
        const requirement = requirements.find(r => r.id === requirementId);
        return {
          ...c,
          mapped_requirements: [...currentMappings, requirement]
        };
      }
    }));
  };

  const getRequirementCoverage = () => {
    const coverage = {};
    requirements.forEach(req => {
      const mappedCases = apiTestCases.filter(c =>
        c.mapped_requirements?.some(r => r.id === req.id)
      );
      coverage[req.id] = {
        count: mappedCases.length,
        percentage: Math.round((mappedCases.length / apiTestCases.length) * 100)
      };
    });
    return coverage;
  };

  const filteredCases = cases.filter(c =>
    filterPriority === 'all' || c.priority === filterPriority
  );

  const requirementCoverage = getRequirementCoverage();
  const totalMappings = cases.reduce((sum, c) => sum + (c.mapped_requirements?.length || 0), 0);
  const avgMappings = cases.length > 0 ? (totalMappings / cases.length).toFixed(1) : 0;

  const stats = {
    totalEpics: wizardTestData?.summary?.total_epics || 0,
    totalStories: wizardTestData?.summary?.total_stories || 0,
    totalTestCases: wizardTestData?.summary?.total_test_cases || 0,
    highPriority: wizardTestData?.summary?.high_priority_test_cases || 0
  };

  if (isLoading) {
    return (
      <PrivateWizardLayout>
        <WizardContainer>
          <div className="wizard-loading">
            <div className="wizard-loading-spinner"></div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', justifyContent: 'center' }}>
              <LuBot size={24} style={{ color: 'var(--primary-color)' }} />
              <p className="wizard-loading-text">AI is analyzing your requirements...</p>
            </div>
            <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', marginTop: '0.5rem' }}>
              Generating test scenarios and steps
            </p>
          </div>
        </WizardContainer>
      </PrivateWizardLayout>
    );
  }

  return (
    <PrivateWizardLayout>
      <WizardContainer>
        <Stepper currentStep={6} completedSteps={engine.flow.completedSteps} />
        
        <WizardCard 
          title={isUIDiscoveryMode ? 'UI Discovery — AI Preview' : 'AI-Generated Test Cases Preview'} 
          subtitle={
            isUIDiscoveryMode
              ? 'Review test cases generated from Auto Discover UI before continuing'
              : 'Review, edit, and refine your test cases before automation'
          }
        >
          {/* Statistics Dashboard */}
          <div className="ai-preview-stats">
            <div className="ai-preview-stat-card">
              <div className="ai-preview-stat-icon">
                <LuActivity size={32} strokeWidth={1.5} />
              </div>
              <div className="ai-preview-stat-content">
                <div className="ai-preview-stat-value">{stats.totalEpics}</div>
                <div className="ai-preview-stat-label">Total Epics</div>
              </div>
            </div>
            <div className="ai-preview-stat-card">
              <div className="ai-preview-stat-icon">
                <LuFileText size={32} strokeWidth={1.5} />
              </div>
              <div className="ai-preview-stat-content">
                <div className="ai-preview-stat-value">{stats.totalStories}</div>
                <div className="ai-preview-stat-label">Total Stories</div>
              </div>
            </div>
            <div className="ai-preview-stat-card">
              <div className="ai-preview-stat-icon">
                <LuTarget size={32} strokeWidth={1.5} />
              </div>
              <div className="ai-preview-stat-content">
                <div className="ai-preview-stat-value">{stats.totalTestCases}</div>
                <div className="ai-preview-stat-label">Total Test Cases</div>
              </div>
            </div>
            <div className="ai-preview-stat-card">
              <div className="ai-preview-stat-icon">
                <LuTrendingUp size={32} strokeWidth={1.5} />
              </div>
              <div className="ai-preview-stat-content">
                <div className="ai-preview-stat-value">{stats.highPriority}</div>
                <div className="ai-preview-stat-label">High Priority</div>
              </div>
            </div>
          </div>

          {/* AI Insights Panel */}
          {showInsights && (
            <div className="ai-preview-insights">
              <div className="ai-preview-insights-header">
                <span className="ai-preview-insights-title">
                  <LuLightbulb size={20} strokeWidth={2} style={{ color: 'var(--warning-color)' }} />
                  AI Insights & Recommendations
                </span>
                <button
                  className="ai-preview-insights-close"
                  onClick={() => setShowInsights(false)}
                >
                  <LuX size={20} />
                </button>
              </div>
              <div className="ai-preview-insights-content">
                <div className="ai-preview-insight-item">
                  <span className="ai-preview-insight-badge success">
                    <LuCheck size={16} />
                  </span>
                  <span>Good test coverage with {stats.totalTestCases} test cases covering key scenarios</span>
                </div>
                <div className="ai-preview-insight-item">
                  <span className="ai-preview-insight-badge warning">
                    <LuInfo size={16} />
                  </span>
                  <span>Consider adding edge cases and error handling scenarios</span>
                </div>
                <div className="ai-preview-insight-item">
                  <span className="ai-preview-insight-badge info">
                    <LuInfo size={16} />
                  </span>
                  <span>{stats.totalStories} stories across {stats.totalEpics} epics requiring test coverage</span>
                </div>
              </div>
            </div>
          )}

          {/* Hierarchical View - Epics, Stories, Test Cases, Steps */}
          {wizardTestData?.epics && wizardTestData.epics.length > 0 && (
            <>
              {/* Priority Filter */}
              <div className="mb-6 flex items-center gap-3">
                <label className="text-sm font-medium text-gray-700">Filter by Priority:</label>
                <select
                  value={filterPriority}
                  onChange={(e) => setFilterPriority(e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-lg bg-white text-gray-900 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                >
                  <option value="all">All Priorities</option>
                  <option value="High">High</option>
                  <option value="Medium">Medium</option>
                  <option value="Low">Low</option>
                </select>
              </div>

              <div className="ai-preview-hierarchy" style={{ marginBottom: '2rem' }}>
                <h3 style={{ fontSize: '1.1rem', fontWeight: 600, marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <LuFileText size={20} />
                  Work Items
                </h3>
              
              <div className="border border-gray-300 rounded-lg overflow-hidden">
                {wizardTestData.epics.map((epic) => (
                  <div key={epic.epic_id} className="border-b border-gray-300 last:border-b-0">
                    <div 
                      onClick={() => setExpandedHierarchy(prev => ({
                        ...prev,
                        epics: { ...prev.epics, [epic.epic_id]: !prev.epics[epic.epic_id] }
                      }))}
                      className="px-4 py-3 bg-gray-50 hover:bg-gray-100 cursor-pointer flex items-center gap-3 select-none transition-colors"
                    >
                      <div className="flex-shrink-0">
                        {expandedHierarchy.epics[epic.epic_id] ? <LuChevronDown size={20} /> : <LuChevronRight size={20} />}
                      </div>
                      <span className="font-semibold text-base text-gray-900 flex-grow">
                        {/* {epic.epic_key} - */}
                         {epic.summary} 
                      </span>
                      <span className="text-xs text-gray-500">
                        {epic.stories?.length || 0} stories
                      </span>
                    </div>

                    {/* Expanded Stories */}
                    {expandedHierarchy.epics[epic.epic_id] && epic.stories && epic.stories.map((story) => (
                      <div key={story.story_id} className="border-t border-gray-200">
                        <div 
                          onClick={() => setExpandedHierarchy(prev => ({
                            ...prev,
                            stories: { ...prev.stories, [story.story_id]: !prev.stories[story.story_id] }
                          }))}
                          className="pl-8 pr-4 py-2 bg-blue-50 hover:bg-blue-100 cursor-pointer flex items-center gap-3 select-none transition-colors"
                        >
                          <div className="flex-shrink-0">
                            {expandedHierarchy.stories[story.story_id] ? <LuChevronDown size={18} /> : <LuChevronRight size={18} />}
                          </div>
                          <span className="font-medium text-sm text-gray-900 flex-grow">
                            {story.story_key} - {story.summary}
                          </span>
                          <span className="text-xs text-gray-500">
                            {story.test_cases?.length || 0} tests
                          </span>
                        </div>

                        {/* Expanded Test Cases */}
                        {expandedHierarchy.stories[story.story_id] && story.test_cases && story.test_cases.map((testCase) => (
                          <div key={testCase.test_case_id} className="border-t border-gray-200">
                            <div 
                              onClick={() => setExpandedHierarchy(prev => ({
                                ...prev,
                                testCases: { ...prev.testCases, [testCase.test_case_id]: !prev.testCases[testCase.test_case_id] }
                              }))}
                              className={`pl-12 pr-4 py-2 cursor-pointer flex items-center gap-3 select-none transition-colors ${
                                testCase.priority === 'High' ? 'bg-red-50 hover:bg-red-100' : testCase.priority === 'Medium' ? 'bg-yellow-50 hover:bg-yellow-100' : 'bg-white hover:bg-gray-50'
                              }`}
                            >
                              <div className="flex-shrink-0">
                                {expandedHierarchy.testCases[testCase.test_case_id] ? <LuChevronDown size={16} /> : <LuChevronRight size={16} />}
                              </div>
                              <span className="text-sm text-gray-900 flex-grow">
                                {testCase.name}
                              </span>
                              <span className={`text-xs px-2 py-1 rounded text-white ${
                                testCase.priority === 'High' ? 'bg-red-500' : testCase.priority === 'Medium' ? 'bg-yellow-500' : 'bg-blue-500'
                              }`}>
                                {testCase.priority}
                              </span>
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteTestCaseClick(testCase);
                                }}
                                title="Delete Test Case"
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  background: 'transparent',
                                  border: 'none',
                                  cursor: 'pointer',
                                  color: 'var(--danger-color, #dc2626)',
                                  padding: '4px'
                                }}
                              >
                                <LuTrash size={16} />
                              </button>
                            </div>

                            {/* Expanded Steps */}
                            {expandedHierarchy.testCases[testCase.test_case_id] && testCase.steps && (
                              <div className="pl-16 pr-4 py-3 bg-gray-50 text-sm space-y-3">
                                <div>
                                  <h6 className="font-semibold text-xs text-gray-700 mb-2">Steps:</h6>
                                  <ul className="list-disc list-inside space-y-1 ml-2">
                                    {testCase.steps.step_description?.map((step, idx) => (
                                      <li key={idx} className="text-xs text-gray-600">
                                        {step}
                                      </li>
                                    )) || (
                                      <li className="text-xs text-gray-500">No steps defined</li>
                                    )}
                                  </ul>
                                </div>
                                <div>
                                  <h6 className="font-semibold text-xs text-gray-700 mb-2">Expected Results:</h6>
                                  <ul className="list-disc list-inside space-y-1 ml-2">
                                    {testCase.steps.expected_result?.map((result, idx) => (
                                      <li key={idx} className="text-xs text-gray-600">
                                        {result}
                                      </li>
                                    )) || (
                                      <li className="text-xs text-gray-500">No results defined</li>
                                    )}
                                  </ul>
                                </div>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </div>
            </>
          )}

          {/* Traceability Matrix Toggle */}
          {requirements.length > 0 && (
            <div className="ai-preview-traceability-toggle">
              <button
                className={`ai-preview-traceability-btn ${showTraceability ? 'active' : ''}`}
                onClick={() => setShowTraceability(!showTraceability)}
              >
                <LuLink size={18} strokeWidth={2} />
                {showTraceability ? 'Hide' : 'Show'} Requirement Traceability
                <span className="ai-preview-badge">{stats.requirements} Requirements</span>
              </button>
            </div>
          )}

          {/* Traceability Matrix Panel */}
          {showTraceability && requirements.length > 0 && (
            <div className="ai-preview-traceability-panel">
              <div className="ai-preview-traceability-header">
                <h3>Requirement Traceability Matrix</h3>
                <p className="ai-preview-traceability-subtitle">
                  Showing how AI-generated test cases map to source requirements ({stats.mappings} total mappings, {avgMappings} avg per test)
                </p>
              </div>

              <div className="ai-preview-requirements-list">
                {requirements.map(req => {
                  const coverage = requirementCoverage[req.id] || { count: 0, percentage: 0 };
                  return (
                    <div key={req.id} className="ai-preview-requirement-card">
                      <div className="ai-preview-requirement-header">
                        <div className="ai-preview-requirement-info">
                          <span className={`ai-preview-requirement-badge ${req.type.toLowerCase()}`}>
                            {req.source}
                          </span>
                          <span className="ai-preview-requirement-key">{req.key}</span>
                          <span className="ai-preview-requirement-title">{req.title}</span>
                        </div>
                        <div className="ai-preview-requirement-coverage">
                          <div className="ai-preview-requirement-coverage-bar">
                            <div 
                              className="ai-preview-requirement-coverage-fill"
                              style={{ width: `${coverage.percentage}%` }}
                            ></div>
                          </div>
                          <span className="ai-preview-requirement-coverage-text">
                            {coverage.count} test{coverage.count !== 1 ? 's' : ''} ({coverage.percentage}%)
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}


          {/* Action Toolbar */}
          {/* <div className="ai-preview-toolbar">
            <div className="ai-preview-toolbar-left">
              <button
                className="ai-preview-toolbar-btn primary"
                onClick={handleAddTestCase}
              >
                <LuPlus size={18} strokeWidth={2} /> Add Test Case
              </button> */}
              {/* {selectedCases.length > 0 && (
                <>
                  <button
                    className="ai-preview-toolbar-btn"
                    onClick={handleRegenerateSelected}
                  >
                    <LuRefreshCw size={18} strokeWidth={2} /> Regenerate ({selectedCases.length})
                  </button>
                  <button
                    className="ai-preview-toolbar-btn danger"
                    onClick={handleDeleteSelected}
                  >
                    <LuTrash size={18} strokeWidth={2} /> Delete ({selectedCases.length})
                  </button>
                </>
              )} */}
            {/* </div> */}
            {/* <div className="ai-preview-toolbar-right">
              <select
                className="ai-preview-filter"
                value={filterPriority}
                onChange={(e) => setFilterPriority(e.target.value)}
              >
                <option value="all">All Priorities</option>
                <option value="High">High Priority</option>
                <option value="Medium">Medium Priority</option>
                <option value="Low">Low Priority</option>
              </select>
              <div className="ai-preview-view-toggle">
                <button
                  className={`ai-preview-view-btn ${viewMode === 'list' ? 'active' : ''}`}
                  onClick={() => setViewMode('list')}
                  title="List View"
                >
                  <LuList size={18} />
                </button>
                <button
                  className={`ai-preview-view-btn ${viewMode === 'grid' ? 'active' : ''}`}
                  onClick={() => setViewMode('grid')}
                  title="Grid View"
                >
                  <LuLayoutGrid size={18} />
                </button>
              </div>
            </div>
          </div>

          <div className="ai-preview-cases-container">
            {filteredCases.length === 0 ? (
              <div className="ai-preview-empty">
                <div className="ai-preview-empty-icon">
                  <LuInbox size={64} strokeWidth={1.5} style={{ color: 'var(--text-tertiary)' }} />
                </div>
                <p>No test cases match the selected filter</p>
              </div>
            ) : (
              <>
                <div className="ai-preview-select-all">
                  <label className="ai-preview-checkbox-label">
                    <input
                      type="checkbox"
                      checked={selectedCases.length === filteredCases.length}
                      onChange={handleSelectAll}
                    />
                    <span>Select All ({filteredCases.length})</span>
                  </label>
                </div>
                
                <div className={`ai-preview-cases ${viewMode === 'grid' ? 'grid-view' : 'list-view'}`}>
                  {filteredCases.map((testCase, index) => (
                    <div 
                      key={testCase.id} 
                      className={`ai-preview-case ${selectedCases.includes(testCase.id) ? 'selected' : ''} ${expandedCase === testCase.id ? 'expanded' : ''}`}
                    >
                      <div className="ai-preview-case-header">
                        <div className="ai-preview-case-header-left">
                          <input
                            type="checkbox"
                            checked={selectedCases.includes(testCase.id)}
                            onChange={() => handleSelectCase(testCase.id)}
                            onClick={(e) => e.stopPropagation()}
                          />
                          <span className="ai-preview-case-number">#{index + 1}</span>
                        </div>
                        <div className="ai-preview-case-header-center">
                          {editingCase === testCase.id ? (
                            <input
                              type="text"
                              className="ai-preview-case-name-input"
                              value={testCase.test_case_name}
                              onChange={(e) => handleUpdateCase(testCase.id, { test_case_name: e.target.value })}
                              onBlur={() => setEditingCase(null)}
                              autoFocus
                            />
                          ) : (
                            <h4 
                              className="ai-preview-case-name"
                              onClick={() => setEditingCase(testCase.id)}
                            >
                              {testCase.test_case_name}
                            </h4>
                          )}
                          <div className="ai-preview-case-meta">
                            <span className="ai-preview-case-steps">{testCase.steps.length} steps</span>
                            <span className="ai-preview-case-dot">•</span>
                            <select 
                              className={`ai-preview-priority-select priority-${testCase.priority.toLowerCase()}`}
                              value={testCase.priority}
                              onChange={(e) => handleUpdateCase(testCase.id, { priority: e.target.value })}
                              onClick={(e) => e.stopPropagation()}
                            >
                              <option value="High">High</option>
                              <option value="Medium">Medium</option>
                              <option value="Low">Low</option>
                            </select>
                          </div>
                        </div>
                        <div className="ai-preview-case-header-right">
                          <div className="ai-preview-case-tags">
                            {testCase.tags.map(tag => (
                              <span key={tag} className="ai-preview-tag">{tag}</span>
                            ))}
                            {testCase.mapped_requirements && testCase.mapped_requirements.length > 0 && (
                              <span className="ai-preview-req-badge" title="Linked Requirements">
                                <LuLink size={14} /> {testCase.mapped_requirements.length}
                              </span>
                            )}
                          </div>
                          <div className="ai-preview-case-actions">
                            {requirements.length > 0 && (
                              <button
                                className="ai-preview-action-btn"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setMappingCase(mappingCase === testCase.id ? null : testCase.id);
                                }}
                                title="Link Requirements"
                              >
                                <LuLink size={16} />
                              </button>
                            )}
                            <button
                              className="ai-preview-action-btn"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDuplicateCase(testCase.id);
                              }}
                              title="Duplicate"
                            >
                              <LuCopy size={16} />
                            </button>
                            <button
                              className="ai-preview-action-btn"
                              onClick={(e) => {
                                e.stopPropagation();
                                setExpandedCase(expandedCase === testCase.id ? null : testCase.id);
                              }}
                              title={expandedCase === testCase.id ? "Collapse" : "Expand"}
                            >
                              {expandedCase === testCase.id ? <LuChevronUp size={16} /> : <LuChevronDown size={16} />}
                            </button>
                          </div>
                        </div>
                      </div>
                      {mappingCase === testCase.id && requirements.length > 0 && (
                        <div className="ai-preview-mapping-panel">
                          <div className="ai-preview-mapping-header">
                            <h5>Link Requirements to This Test Case</h5>
                            <button
                              className="ai-preview-mapping-close"
                              onClick={() => setMappingCase(null)}
                            >
                              <LuX size={18} />
                            </button>
                          </div>
                          <div className="ai-preview-mapping-list">
                            {requirements.map(req => {
                              const isMapped = testCase.mapped_requirements?.some(r => r.id === req.id);
                              return (
                                <label 
                                  key={req.id} 
                                  className={`ai-preview-mapping-item ${isMapped ? 'mapped' : ''}`}
                                >
                                  <input
                                    type="checkbox"
                                    checked={isMapped}
                                    onChange={() => handleToggleRequirement(testCase.id, req.id)}
                                  />
                                  <div className="ai-preview-mapping-req-info">
                                    <span className={`ai-preview-mapping-badge ${req.type.toLowerCase()}`}>
                                      {req.source}
                                    </span>
                                    <span className="ai-preview-mapping-key">{req.key}</span>
                                    <span className="ai-preview-mapping-title">{req.title}</span>
                                  </div>
                                  {isMapped && (
                                    <span className="ai-preview-mapping-check">
                                      <LuCheck size={16} />
                                    </span>
                                  )}
                                </label>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {expandedCase === testCase.id && (
                        <div className="ai-preview-case-details">
                          {testCase.mapped_requirements && testCase.mapped_requirements.length > 0 && (
                            <div className="ai-preview-linked-requirements">
                              <h5>Linked Requirements</h5>
                              <div className="ai-preview-linked-req-list">
                                {testCase.mapped_requirements.map(req => (
                                  <div key={req.id} className="ai-preview-linked-req">
                                    <span className={`ai-preview-linked-req-badge ${req.type.toLowerCase()}`}>
                                      {req.source}
                                    </span>
                                    <span className="ai-preview-linked-req-key">{req.key}</span>
                                    <span className="ai-preview-linked-req-title">{req.title}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          <div className="ai-preview-steps-header">
                            <h5>Test Steps</h5>
                            <button 
                              className="ai-preview-add-step-btn"
                              onClick={() => handleAddStep(testCase.id)}
                            >
                              + Add Step
                            </button>
                          </div>
                          <div className="ai-preview-steps">
                            {testCase.steps.map((step, stepIndex) => (
                              <div key={stepIndex} className="ai-preview-step">
                                <div className="ai-preview-step-number">{step.step}</div>
                                <div className="ai-preview-step-content">
                                  {editingStep === `${testCase.id}-${stepIndex}` ? (
                                    <>
                                      <div className="ai-preview-step-field">
                                        <label>Action:</label>
                                        <input
                                          type="text"
                                          value={step.action}
                                          onChange={(e) => handleUpdateStep(testCase.id, stepIndex, { action: e.target.value })}
                                          placeholder="Enter action"
                                        />
                                      </div>
                                      <div className="ai-preview-step-field">
                                        <label>Expected Result:</label>
                                        <input
                                          type="text"
                                          value={step.expected}
                                          onChange={(e) => handleUpdateStep(testCase.id, stepIndex, { expected: e.target.value })}
                                          placeholder="Enter expected result"
                                        />
                                      </div>
                                      <button
                                        className="ai-preview-step-save"
                                        onClick={() => setEditingStep(null)}
                                      >
                                        <LuCheck size={16} /> Save
                                      </button>
                                    </>
                                  ) : (
                                    <>
                                      <div className="ai-preview-step-info">
                                        <div className="ai-preview-step-action">
                                          <strong>Action:</strong> {step.action}
                                        </div>
                                        <div className="ai-preview-step-expected">
                                          <strong>Expected:</strong> {step.expected}
                                        </div>
                                      </div>
                                      <div className="ai-preview-step-actions">
                                        <button
                                          className="ai-preview-step-action-btn"
                                          onClick={() => setEditingStep(`${testCase.id}-${stepIndex}`)}
                                          title="Edit"
                                        >
                                          <LuPencil size={14} />
                                        </button>
                                        <button 
                                          className="ai-preview-step-action-btn danger"
                                          onClick={() => handleDeleteStep(testCase.id, stepIndex)}
                                          title="Delete"
                                        >
                                          🗑️
                                        </button>
                                      </div>
                                    </>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </>
            )} */}
          {/* </div> */}

          {/* UI Map Warning Modal */}
          {showUIMapWarning && (
            <div data-rt-modal-overlay className="wizard-modal-overlay"
              onClick={() => setShowUIMapWarning(false)}
              style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 9999
              }}
            >
              <div 
                className="wizard-modal"
                onClick={(e) => e.stopPropagation()}
                style={{
                  backgroundColor: '#ffffff',
                  borderRadius: '16px',
                  padding: '2rem',
                  maxWidth: '560px',
                  width: '90%',
                  boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
                  position: 'relative',
                  zIndex: 10000
                }}
              >
                <div style={{ marginBottom: '1.5rem' }}>
                  <div style={{
                    width: '64px',
                    height: '64px',
                    borderRadius: '50%',
                    backgroundColor: 'var(--warning-color-light)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 1rem'
                  }}>
                    <LuInfo size={32} style={{ color: 'var(--warning-color)' }} />
                  </div>
                  <h3 style={{ fontSize: '1.25rem', fontWeight: 600, marginBottom: '0.5rem', textAlign: 'center', color: 'var(--text-primary)' }}>
                    Cannot Generate Scripts Without UI Map
                  </h3>
                  <p style={{ color: 'var(--text-secondary)', textAlign: 'center', marginBottom: '1.5rem', fontSize: '0.95rem' }}>
                    For {project.applicationType} projects, automation scripts require a UI Map containing DOM structure,
                    selectors, and navigation flows.
                  </p>
                  <div style={{
                    padding: '1rem',
                    backgroundColor: 'var(--info-color-light)',
                    borderRadius: '8px',
                    marginBottom: '1.5rem'
                  }}>
                    <p style={{ fontSize: '0.9rem', margin: '0 0 0.5rem 0', color: 'var(--text-primary)', fontWeight: 600 }}>
                      Your options:
                    </p>
                    <ul style={{ margin: '0.5rem 0 0', paddingLeft: '1.25rem', fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                      <li>Record a test flow to build the UI Map</li>
                      <li>Run UI Discovery to automatically map your application</li>
                      <li>Save test cases now and generate scripts later</li>
                    </ul>
                  </div>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                  {/* <button
                    className="wizard-btn wizard-btn-primary"
                    onClick={() => {
                      setShowUIMapWarning(false);
                      navigate('/app/onboarding?step=6&mode=recorder');
                    }}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '0.5rem'
                    }}
                  >
                    <LuVideo size={18} />
                    Record Test Flow
                  </button> */}
                  <button
                    className="wizard-btn wizard-btn-secondary"
                    onClick={() => {
                      setShowUIMapWarning(false);
                      navigate('/app/onboarding?step=6&mode=discovery');
                    }}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '0.5rem'
                    }}
                  >
                    <LuRadar size={18} />
                    Run UI Discovery
                  </button>
                  <button
                    className="wizard-btn wizard-btn-secondary"
                    onClick={handleTestCasesOnly}
                    disabled={isSaving}
                  >
                    Save Test Cases Only (No Scripts)
                  </button>
                  <button
                    className="wizard-btn"
                    onClick={() => setShowUIMapWarning(false)}
                    style={{
                      backgroundColor: 'transparent',
                      border: '1px solid var(--border-color)',
                      color: 'var(--text-primary)'
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}

          <DeleteConfirmationModal
            isOpen={showDeleteConfirm}
            itemName={testCaseToDelete?.name}
            itemType="test case"
            onCancel={() => {
              setShowDeleteConfirm(false);
              setTestCaseToDelete(null);
            }}
            onConfirm={confirmDeleteTestCase}
            isProcessing={isDeleting}
          />

          <WizardActions
            showBack={true}
            onBack={() => {
              previousStep();
              navigate('/app/onboarding?step=5');
            }}
            showNext={true}
            onNext={handleContinue}
            nextLabel={`Approve & Continue (${cases.length} test cases)`}
            nextLoading={isSaving}
          />
        </WizardCard>
      </WizardContainer>
    </PrivateWizardLayout>
  );
};

export default Step6AAIPreview;